import { merge, forkJoin } from "rxjs";
import {
  map as rxMap,
  filter,
  mapTo,
  catchError,
  takeUntil,
  mergeMap,
  debounceTime,
} from "rxjs/operators";

import querystring from "querystring";
import {
  isEmpty,
  join,
  map,
  mergeAll,
  mergeWith,
  pipe,
  pluck,
  zipObj,
} from "ramda";
import { combineStrings } from "util/ext";
import { combineEpics, ofType } from "redux-observable";
import { API_BASE_URL } from "props";
import { ajaxGetAllModules, apiAjax, errorToAction } from "util/ajaxUtil";
import * as applicationActionTypes from "application/redux/applicationActionTypes";
import * as browseMediaActionTypes from "browse-media/redux/browseMediaActionTypes";
import {
  addModulesDrawerModulesLoaded,
  applyAddModulesDrawerFilterQuery,
  mediaItemsSynced,
  removeModulesDrawerModulesLoaded,
  search,
  searchResultsLoaded,
} from "browse-media/redux/browseMediaActions";

const POST_MODULES_BATCH_ASSIGN_URL = `${API_BASE_URL}/admin/modules/batch-assign`;

const getModuleUrl = moduleId => `${API_BASE_URL}/admin/modules/${moduleId}`;

const getTermExpressions = map(facet =>
  pipe(
    pluck("code"),
    map(code => (facet.queryParam === "_pd" ? code : `"${code}"`)),
    join(" OR "),
  )(facet.terms),
);

const getYearGroupExpression = pipe(pluck("expression"), join(" OR "));

const getFacetTermCodeExpressions = facets =>
  zipObj(pluck("queryParam", facets), getTermExpressions(facets));

const mergeQueryExpressions = mergeWith(combineStrings(" OR "));

const getSearchParameters = (
  query,
  facets,
  publicationYearGroups,
  offset,
  limit,
) => {
  const termCodesQueryExpressions = getFacetTermCodeExpressions(facets);
  const yearGroupQueryExpression = !isEmpty(publicationYearGroups)
    ? { _pd: getYearGroupExpression(publicationYearGroups) }
    : {};

  const facetQueryExpressions = mergeQueryExpressions(
    yearGroupQueryExpression,
    termCodesQueryExpressions,
  );

  return mergeAll([
    isEmpty(query) ? { g: "*" } : { g: query },
    facetQueryExpressions,
    { offset },
    { limit },
  ]);
};

const getSearchUrl = (query, facets, publicationYearGroups, offset, limit) => {
  const params = getSearchParameters(
    query,
    facets,
    publicationYearGroups,
    offset,
    limit,
  );
  return `${API_BASE_URL}/admin/search?${querystring.encode(params)}`;
};

const ajaxAddModules = state => (mediaIds, moduleIds) =>
  apiAjax(state)({
    url: POST_MODULES_BATCH_ASSIGN_URL,
    method: "POST",
    body: {
      adds: {
        mediaIds,
        moduleIds,
      },
    },
  }).pipe(rxMap(response => mediaIds));

const ajaxRemoveModules = state => (mediaIds, moduleIds) =>
  apiAjax(state)({
    url: POST_MODULES_BATCH_ASSIGN_URL,
    method: "POST",
    body: {
      deletes: {
        mediaIds,
        moduleIds,
      },
    },
  }).pipe(rxMap(response => mediaIds));

const ajaxGetSearchResults = state => (
  query,
  facets,
  publicationYearGroups,
  offset,
  limit,
) =>
  apiAjax(state)({
    url: getSearchUrl(query, facets, publicationYearGroups, offset, limit),
  }).pipe(rxMap(ajaxResponse => ajaxResponse.response));

const ajaxGetModule = state => moduleId =>
  apiAjax(state)({
    url: getModuleUrl(moduleId),
  }).pipe(rxMap(ajaxResponse => ajaxResponse.response));

const ajaxGetModules = state => moduleIds =>
  forkJoin(...moduleIds.map(ajaxGetModule(state)));

const addModulesDrawerFilterQueryChangedEpic = action$ =>
  action$.pipe(
    ofType(browseMediaActionTypes.ADD_MODULES_DRAWER_FILTER_QUERY_CHANGED),
    debounceTime(100),
    rxMap(applyAddModulesDrawerFilterQuery),
  );

const addSelectedModulesEpic = (action$, state$) =>
  action$.pipe(
    ofType(browseMediaActionTypes.ADD_SELECTED_MODULES),
    mergeMap(() => {
      const { browseMedia } = state$.value;
      const { selectedMediaIds } = browseMedia;
      const { selectedModuleIds } = browseMedia.addModulesDrawer;

      console.log(
        "ajaxAddModules, selectedMediaIds: %O, selectedModuleIds: %O",
        selectedMediaIds,
        selectedModuleIds,
      );

      return ajaxAddModules(state$.value)(
        selectedMediaIds,
        selectedModuleIds,
      ).pipe(
        rxMap(() => mediaItemsSynced(selectedMediaIds)),
        catchError(errorToAction),
      );
    }),
  );

const removeSelectedModulesEpic = (action$, state$) =>
  action$.pipe(
    ofType(browseMediaActionTypes.REMOVE_SELECTED_MODULES),
    mergeMap(() => {
      const { browseMedia } = state$.value;
      const { selectedMediaIds } = browseMedia;
      const { selectedModuleIds } = browseMedia.removeModulesDrawer;

      return ajaxRemoveModules(state$.value)(
        selectedMediaIds,
        selectedModuleIds,
      ).pipe(
        rxMap(() => mediaItemsSynced(selectedMediaIds)),
        catchError(errorToAction),
      );
    }),
  );

const searchEpic = (action$, state$) =>
  action$.pipe(
    filter(
      action =>
        action.type === browseMediaActionTypes.APPLY_FACET_DIALOG_FACET ||
        action.type === browseMediaActionTypes.LIMIT_CHANGE ||
        action.type === browseMediaActionTypes.OFFSET_CHANGE ||
        action.type === browseMediaActionTypes.REMOVE_FACET ||
        action.type === browseMediaActionTypes.REMOVE_QUERY ||
        action.type === browseMediaActionTypes.SEARCH,
    ),
    mergeMap(() => {
      const state = state$.value;
      const {
        appliedQuery,
        appliedFacets,
        appliedPublicationYearGroups,
        offset,
        limit,
      } = state.browseMedia;

      return ajaxGetSearchResults(state)(
        appliedQuery,
        appliedFacets,
        appliedPublicationYearGroups,
        offset,
        limit,
      ).pipe(
        rxMap(searchResultsLoaded),
        catchError(errorToAction),
        takeUntil(
          merge(
            action$.pipe(
              ofType(
                browseMediaActionTypes.APPLY_FACET_DIALOG_FACET,
                browseMediaActionTypes.LIMIT_CHANGE,
                browseMediaActionTypes.OFFSET_CHANGE,
                browseMediaActionTypes.REMOVE_FACET,
                browseMediaActionTypes.REMOVE_QUERY,
                browseMediaActionTypes.SEARCH,
              ),
            ),
          ),
        ),
      );
    }),
  );

const showAddModulesDrawerEpic = (action$, state$) =>
  action$.pipe(
    ofType(browseMediaActionTypes.SHOW_ADD_MODULES_DRAWER),
    mergeMap(() =>
      ajaxGetAllModules(state$.value).pipe(
        rxMap(addModulesDrawerModulesLoaded),
        catchError(errorToAction),
        takeUntil(
          action$.pipe(ofType(browseMediaActionTypes.HIDE_ADD_MODULES_DRAWER)),
        ),
      ),
    ),
  );

const showRemoveModulesDrawerEpic = (action$, state$) =>
  action$.pipe(
    filter(
      action =>
        action.type === browseMediaActionTypes.SHOW_REMOVE_MODULES_DRAWER ||
        action.type ===
          browseMediaActionTypes.SHOW_REMOVE_MODULES_DRAWER_FOR_SELECTIONS,
    ),
    mergeMap(() => {
      const { selectedItemsModuleIds } = state$.value.browseMedia;

      return ajaxGetModules(state$.value)(selectedItemsModuleIds).pipe(
        rxMap(removeModulesDrawerModulesLoaded),
        catchError(errorToAction),
        takeUntil(
          action$.pipe(
            ofType(browseMediaActionTypes.HIDE_REMOVE_MODULES_DRAWER),
          ),
        ),
      );
    }),
  );

const routeChangedEpic = (action$, state$) =>
  action$.pipe(
    filter(
      action =>
        action.type === applicationActionTypes.ROUTE_CHANGED &&
        action.path === "/browse-media",
    ),
    mapTo(search()),
  );

export default combineEpics(
  addModulesDrawerFilterQueryChangedEpic,
  addSelectedModulesEpic,
  removeSelectedModulesEpic,
  routeChangedEpic,
  searchEpic,
  showAddModulesDrawerEpic,
  showRemoveModulesDrawerEpic,
);
